import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { supabase } from "@/supabase";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import { scoreSnapshot, aggregateSession } from "@/utils/gameStats";
import { saveGameSession } from "@/services/statsService";
import {
  HAND_SIZE,
  TOP_UP_COUNT,
  buildHand,
  buildTopUp,
  orderDrawPile,
  dealLogRows,
} from "@/utils/dealCards";
import {
  recentCardIds,
  recordGameCards,
  recentSnapshot,
} from "@/utils/dealtHistory";

export const useGameStore = defineStore("game", () => {
  // ✅ Feature Flags
  const { isEnabled } = useFeatureFlags();
  
  // ✅ Reactive State
  const timerLength = ref(120);
  const secondaryTimerLength = ref(60);
  const timeRemaining = ref(timerLength.value);
  const timerRunning = ref(false);
  const gameStarted = ref(false);
  const resetCounter = ref(0);
  const finishGame = ref(false);
  const cards = ref([]); // Game cards (shuffled)
  const availableCards = ref([]);
  const jobsDone = ref([]);
  const allCards = ref([]); // All cards for management (database order)
  const showTiers = ref(false);
  const selectionEnabled = ref(false);
  const isDiscussionMode = ref(true);
  const allowJobDrop = ref(false);
  const allowCardReordering = ref(false);
  const maxJobsDoneCount = ref(0);
  const showPrompts = ref(true);
  const promptsDisabledAfterRound2 = ref(false);
  const showExplanations = ref(false);
  const editingCard = ref(null);
  const activeModal = ref(null);
  const selectedCards = ref([]);//Reactive array to track selected cards
  const storedDepartment = localStorage.getItem('selectedDepartment');
  const selectedDepartment = ref(
    storedDepartment && !Number.isNaN(Number(storedDepartment))
      ? Number(storedDepartment)
      : null
  );
  const isDoneToggled = ref(false); // Track if "I'm Done" has been clicked
  const roundNumber = ref(0); // Track which round we're on
  const showJobs = ref(false); // Track if jobs done container is expanded
  const cardHeadingFontSize = ref(Number(localStorage.getItem('cardHeadingFontSize')) || 100); // Font size percentage (100 = default)
  const isResetting = ref(false); // Track reset animation state
  const showExplanationsWhenDone = ref(true); // Control whether to show explanations when "I'm Done" is pressed
  const showTiersWhenDone = ref(true); // Control whether to show tiers when "I'm Done" is pressed
  const showCountdown = ref(false); // Show countdown overlay
  const countdownNumber = ref(3); // Current countdown number
  // Notes feature state
  const notes = ref([]);
  const notesChannel = ref(null);
  const sessionId = ref(localStorage.getItem('notes_session_id') || '');
  
  // Departments state
  const departments = ref([]);

  /** When true, fetchCardsByDepartment only loads cards with use_for_demo. Synced from localStorage gameSession. */
  const isDemoSession = ref(false);

  const applyGameSessionFromStorage = () => {
    try {
      const raw = localStorage.getItem("gameSession");
      if (!raw) {
        isDemoSession.value = false;
        return;
      }
      const s = JSON.parse(raw);
      // isDemo is true for code-based demo sessions AND for email users with demo_only access
      isDemoSession.value = !!(s.isDemo);
    } catch {
      isDemoSession.value = false;
    }
  };
  
  // Analytics state
  const gameAnalytics = ref({
    gameStartTime: null,
    gameEndTime: null,
    roundTimes: [], // Array of round completion times
    totalRoundsCompleted: 0,
    finalCardOrder: [],
    totalGameTime: 0, // Actual gameplay time in milliseconds
    lastResumeTime: null, // Track when timer was last resumed
    validRowsHistory: [], // Track valid rows each time "I'm Done" is pressed
    deals: [], // Scored snapshot per deal, keyed by round number
    dealtAt: null, // When the current round's cards were dealt
    cardsDealtIds: [], // Card ids that hit the table this game (for local history)
  });
  const showGameSummary = ref(false);
  /** When set, CardContainer runs a horizontal FLIP shuffle into this hand order. */
  const pendingShuffleTo = ref(null);
  
  let timerInterval = null;

  const logDeal = (label, payload = {}) => {
    if (!isEnabled("DEBUG_MODE")) return;
    console.groupCollapsed(`[deal] ${label}`);
    if (payload.rows) console.table(payload.rows);
    const rest = { ...payload };
    delete rest.rows;
    if (Object.keys(rest).length) console.log(rest);
    console.groupEnd();
  };

  const trackDealtIds = (dealtCards) => {
    const ids = (dealtCards || []).map((c) => c?.id).filter((id) => id != null);
    if (!ids.length) return;
    const existing = new Set(gameAnalytics.value.cardsDealtIds || []);
    ids.forEach((id) => existing.add(id));
    gameAnalytics.value.cardsDealtIds = [...existing];
  };

  const persistDealtHistory = () => {
    const ids = gameAnalytics.value.cardsDealtIds || [];
    if (!ids.length) return;
    const snapshot = recordGameCards(
      selectedDepartment.value,
      isDemoSession.value,
      ids
    );
    logDeal("history saved", {
      departmentId: selectedDepartment.value,
      isDemo: isDemoSession.value,
      cardIds: ids,
      last5: snapshot,
    });
    // Clear so a later Reset does not record the same game twice
    gameAnalytics.value.cardsDealtIds = [];
  };

  /**
   * Split a fetched deck into opening hand + draw pile, preferring cards not
   * seen in the last 5 games on this device and capping any tier at 2.
   */
  const dealOpeningFromDeck = (deck, { context = "fetch" } = {}) => {
    const excludeIds = recentCardIds(selectedDepartment.value, isDemoSession.value);
    const { hand, remaining, meta } = buildHand(deck, HAND_SIZE, {
      maxPerTier: 2,
      excludeIds,
    });
    cards.value = hand.map((card) => ({
      ...card,
      isFlipped: false,
      isSelected: false,
      selectable: false,
      isFalling: false,
      isAnimatingOut: false,
      isAnimatingIn: false,
    }));
    availableCards.value = orderDrawPile(remaining, excludeIds);
    logDeal(`${context} opening hand`, {
      departmentId: selectedDepartment.value,
      isDemo: isDemoSession.value,
      excludeIds,
      meta,
      rows: dealLogRows(cards.value),
      drawPileLeft: availableCards.value.length,
      recentHistory: recentSnapshot(selectedDepartment.value, isDemoSession.value),
    });
  };

  // ✅ Computed properties
  const isTimerZero = computed(() => timeRemaining.value === 0);
  const canDealMoreCards = computed(() => cards.value.length === 2);
  const noMoreCards = computed(() => availableCards.value.length < 3);
  
  // Analytics computed properties
  const averageRoundTime = computed(() => {
    if (gameAnalytics.value.roundTimes.length === 0) return 0;
    const total = gameAnalytics.value.roundTimes.reduce((sum, time) => sum + time, 0);
    return Math.round(total / gameAnalytics.value.roundTimes.length);
  });
  
  const validRowsCount = computed(() => {
    // Use the latest entry from validRowsHistory if available
    const history = gameAnalytics.value.validRowsHistory;
    if (history && history.length > 0) {
      return history[history.length - 1].validRows;
    }
    // Fallback to calculating from final card order
    return countValidCardRows(gameAnalytics.value.finalCardOrder);
  });

  // Computed property to get current department's primary color
  const currentDepartmentPrimaryColor = computed(() => {
    if (!selectedDepartment.value) return '#07418C'; // Default blue
    const dept = departments.value.find(d => d.id === selectedDepartment.value);
    return dept?.primary_color || '#07418C';
  });

  // ✅ Analytics Helper Functions
  const countValidCardRows = (cardOrder) => {
    if (!cardOrder || cardOrder.length === 0) {
      return 0;
    }
    
    let validRows = 0;
    for (let i = 0; i <= cardOrder.length - 5; i++) {
      const row = cardOrder.slice(i, i + 5);
      if (isValidCardRow(row)) {
        validRows++;
      }
    }
    return validRows;
  };
  
  const isValidCardRow = (row) => {
    if (row.length !== 5) return false;
    
    // Find the minimum tier in the row
    const minTier = Math.min(...row.map(card => card.ranking));
    
    // Check if the first card has the minimum tier
    if (row[0].ranking !== minTier) return false;
    
    // Check if each subsequent card has the same or higher tier
    for (let i = 1; i < row.length; i++) {
      if (row[i].ranking < row[i - 1].ranking) {
        return false;
      }
    }
    
    return true;
  };
  
  const initializeGameAnalytics = () => {
    const now = Date.now();
    gameAnalytics.value = {
      gameStartTime: now,
      gameEndTime: null,
      roundTimes: [],
      totalRoundsCompleted: 0,
      finalCardOrder: [],
      totalGameTime: 0,
      lastResumeTime: now,
      validRowsHistory: [],
      deals: [],
      dealtAt: now,
      cardsDealt: cards.value.length,
      cardsDealtIds: cards.value.map((c) => c?.id).filter((id) => id != null),
    };
    logDeal("game start / opening deal recorded", {
      rows: dealLogRows(cards.value),
      cardsDealtIds: gameAnalytics.value.cardsDealtIds,
    });
  };

  /** Timestamp the moment a round's cards hit the table, for per-deal timing. */
  const markDealt = () => {
    gameAnalytics.value.dealtAt = Date.now();
  };

  /**
   * Score the current hand against the tier-sorted ideal and store it against
   * the current round. "I'm Done" can be toggled off and pressed again, so this
   * replaces any existing entry for the round rather than appending a duplicate.
   */
  const recordDealSnapshot = () => {
    const tiers = cards.value
      .map((card) => Number(card?.ranking))
      .filter((tier) => Number.isFinite(tier));

    if (tiers.length === 0) return;

    const completedAt = Date.now();
    const dealtAt = gameAnalytics.value.dealtAt;

    const entry = {
      roundNumber: roundNumber.value,
      dealtAt,
      completedAt,
      durationMs: dealtAt ? completedAt - dealtAt : 0,
      tiers,
      ...scoreSnapshot(tiers)
    };

    const deals = gameAnalytics.value.deals;
    const existing = deals.findIndex((d) => d.roundNumber === entry.roundNumber);

    if (existing === -1) {
      deals.push(entry);
    } else {
      deals.splice(existing, 1, entry);
    }
  };
  
  const recordRoundCompletion = () => {
    if (gameAnalytics.value.gameStartTime) {
      const roundTime = Date.now() - gameAnalytics.value.gameStartTime;
      gameAnalytics.value.roundTimes.push(roundTime);
      gameAnalytics.value.totalRoundsCompleted++;
      
      // Reset start time for next round
      gameAnalytics.value.gameStartTime = Date.now();
    }
  };
  
  const recordValidRowsAtCurrentState = () => {
    // Ensure analytics are initialized
    if (!gameAnalytics.value.validRowsHistory) {
      initializeGameAnalytics();
    }
    
    // Record the current card order and valid rows count when "I'm Done" is pressed
    const currentValidRows = countValidCardRows(cards.value);
    gameAnalytics.value.validRowsHistory.push({
      round: roundNumber.value,
      validRows: currentValidRows,
      cardOrder: [...cards.value], // Copy of current card order
      timestamp: Date.now()
    });
    
    console.log(`Round ${roundNumber.value}: ${currentValidRows} valid rows found from ${cards.value.length} cards`);

    recordDealSnapshot();
  };
  
  const pauseGameTimer = () => {
    // Add elapsed time since last resume to total game time
    if (gameAnalytics.value.lastResumeTime) {
      const elapsedTime = Date.now() - gameAnalytics.value.lastResumeTime;
      gameAnalytics.value.totalGameTime += elapsedTime;
      console.log('Game timer paused. Elapsed time:', elapsedTime, 'Total time:', gameAnalytics.value.totalGameTime);
      gameAnalytics.value.lastResumeTime = null;
    }
  };
  
  const resumeGameTimer = () => {
    // Mark the time when timer resumes
    gameAnalytics.value.lastResumeTime = Date.now();
    console.log('Game timer resumed at:', gameAnalytics.value.lastResumeTime);
  };
  
  const finalizeGameAnalytics = () => {
    // Add any remaining time if game is currently running
    if (gameAnalytics.value.lastResumeTime) {
      const elapsedTime = Date.now() - gameAnalytics.value.lastResumeTime;
      gameAnalytics.value.totalGameTime += elapsedTime;
      gameAnalytics.value.lastResumeTime = null; // Clear the resume time
    }
    
    gameAnalytics.value.gameEndTime = Date.now();
    gameAnalytics.value.finalCardOrder = [...cards.value];
    
    // Debug logging
    console.log('Analytics finalized:', {
      totalGameTime: gameAnalytics.value.totalGameTime,
      roundTimes: gameAnalytics.value.roundTimes,
      finalCardOrder: gameAnalytics.value.finalCardOrder.map(c => ({ title: c.title, ranking: c.ranking }))
    });
    
    persistGameStatistics();
    persistDealtHistory();

    showGameSummary.value = true;
  };

  /** Session summary for the current game, shared by the modal and persistence. */
  const sessionStats = computed(() => ({
    ...aggregateSession(gameAnalytics.value.deals),
    totalPlayMs: gameAnalytics.value.totalGameTime,
    cardsDealt: gameAnalytics.value.cardsDealt || 0
  }));

  /**
   * Save the finished game. Skipped for admins and game-code players, who have
   * no Supabase session to record against. Never throws into gameplay.
   */
  const persistGameStatistics = () => {
    if (!isEnabled('USER_STATS')) return;

    const summary = {
      ...aggregateSession(gameAnalytics.value.deals),
      totalPlayMs: gameAnalytics.value.totalGameTime,
      cardsDealt: gameAnalytics.value.cardsDealt || 0
    };

    if (summary.dealsCount === 0) return;

    saveGameSession({
      summary,
      deals: gameAnalytics.value.deals,
      departmentId: selectedDepartment.value,
      isDemo: isDemoSession.value,
      startedAt: gameAnalytics.value.gameStartTime
    }).catch((error) => {
      console.error('Could not save game statistics:', error);
    });
  };

  const setSelectedDepartment = (departmentId) => {
    const depId = Number(departmentId);
    if (Number.isFinite(depId)) {
      selectedDepartment.value = depId;
      localStorage.setItem('selectedDepartment', String(depId));
    } else {
      selectedDepartment.value = null;
      localStorage.removeItem('selectedDepartment');
    }

    // Clear current game state so previous cards don't flash after selecting a new game
    cards.value = [];
    availableCards.value = [];
    jobsDone.value = [];
    gameStarted.value = false;
    allowJobDrop.value = false;
    allowCardReordering.value = false;
    showExplanations.value = false;
    showTiers.value = false;
    // keep prompts enabled for fresh game start
    showPrompts.value = true;
    // reset round and timers for a clean start
    roundNumber.value = 0;
    stopTimer();
    timeRemaining.value = timerLength.value;
  };

  // Fetch departments for timer color
  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .order('position', { ascending: true });
      if (error) throw error;
      departments.value = data || [];
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // ✅ Timer Methods
  const startTimer = () => {
    if (timerRunning.value || timerInterval) return;
    timerRunning.value = true;
    resumeGameTimer(); // Track game time when timer starts
    timerInterval = setInterval(() => {
      decreaseTimer();
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerInterval);
    timerInterval = null;
    timerRunning.value = false;
    pauseGameTimer(); // Track game time when timer stops
  };



  const pauseTimer = () => {
    stopTimer();
  };

  const decreaseTimer = () => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      stopTimer();
      finishGame.value = true;
      applyDoneSettings(); // Use the new settings function
      gameStarted.value = false;
      // Finalize analytics when game ends due to timer
      finalizeGameAnalytics();
    }
  };

  // Ensure we have a session id for notes grouping
  const ensureNotesSessionId = () => {
    if (!sessionId.value) {
      const newId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionId.value = newId;
      localStorage.setItem('notes_session_id', newId);
    }
    return sessionId.value;
  };

  // Notes CRUD
  const fetchNotes = async () => {
    try {
      const sid = ensureNotesSessionId();
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('session_id', sid)
        .order('created_at', { ascending: true });
      if (error) throw error;
      notes.value = data || [];
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const addNote = async (content, author = '') => {
    if (!content || !content.trim()) return;
    try {
      const sid = ensureNotesSessionId();
      const { data, error } = await supabase
        .from('notes')
        .insert([{ session_id: sid, author, content }])
        .select()
        .single();
      if (error) throw error;
      if (data) notes.value.push(data);
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const updateNote = async (noteId, content) => {
    try {
      const { data, error } = await supabase
        .from('notes')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', noteId)
        .select()
        .single();
      if (error) throw error;
      const idx = notes.value.findIndex(n => n.id === noteId);
      if (idx !== -1 && data) notes.value[idx] = data;
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const { error } = await supabase.from('notes').delete().eq('id', noteId);
      if (error) throw error;
      notes.value = notes.value.filter(n => n.id !== noteId);
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // Realtime subscription
  const subscribeToNotes = () => {
    const sid = ensureNotesSessionId();
    if (notesChannel.value) return;
    const channel = supabase.channel(`notes_${sid}`).on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'notes', filter: `session_id=eq.${sid}` },
      (payload) => {
        if (payload.eventType === 'INSERT') {
          notes.value.push(payload.new);
        } else if (payload.eventType === 'UPDATE') {
          const idx = notes.value.findIndex(n => n.id === payload.new.id);
          if (idx !== -1) notes.value[idx] = payload.new;
        } else if (payload.eventType === 'DELETE') {
          notes.value = notes.value.filter(n => n.id !== payload.old.id);
        }
      }
    ).subscribe();
    notesChannel.value = channel;
  };

  const unsubscribeFromNotes = async () => {
    if (notesChannel.value) {
      await supabase.removeChannel(notesChannel.value);
      notesChannel.value = null;
    }
  };

  const updateTimer = (newTimerValue) => {
    timerLength.value = Number(newTimerValue);
    timeRemaining.value = timerLength.value; // ✅ Ensure reset updates the timer
  };

  const updateSecondaryTimer = (newSecondaryTimerValue) => {
    secondaryTimerLength.value = Number(newSecondaryTimerValue);
    //timeRemaining.value = secondaryTimerLength.value; // ✅ Ensure reset updates the timer
  };


  const fetchCardsByDepartment = async (departmentId) => {
    try {
      applyGameSessionFromStorage();

      // If your foreign key is numeric and departmentId is a string, convert it:
      const depId = Number(departmentId);

      let query = supabase
        .from("cards")
        .select("*, department:departments(name, front_image, primary_color, secondary_color)")
        .eq("departmentId", depId);

      if (isDemoSession.value) {
        query = query.eq("use_for_demo", true);
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformedCards = (data || []).map((card) => ({
        id: card.id,
        title: card.title || "",
        content: card.content || "",
        explanation: card.explanation || "",
        ranking: card.ranking || 0,
        // Extract the department name from the joined result:
        department: card.department ? card.department.name : "",
        // Add card image fields (use card-specific images or fallback to department images)
        frontImage: card.front_image || (card.department ? card.department.front_image : ""),
        primaryColor: card.department ? card.department.primary_color : "#000000",
        secondaryColor: card.department ? card.department.secondary_color : "#FFFFFF",
        isFlipped: false,
        isSelected: false,
        selectable: false,
        isFalling: false,
        isAnimatingOut: false,
        isAnimatingIn: false,
      }));

      dealOpeningFromDeck(transformedCards, { context: "department fetch" });
    } catch (error) {
      console.error("Error fetching cards by department:", error);
    }
  };
  

  // ✅ Card Fetching & Management
  const fetchCards = async () => {
    try {
      applyGameSessionFromStorage();
      const { data, error } = await supabase.from("cards") .select("*, department:departments(name)");
      if (error) throw error;

      const transformedCards = data.map((card) => ({
        id: card.id,
        title: card.title || "",
        content: card.content || "",
        explanation: card.explanation || "",
        ranking: card.ranking || 0,
        department: card.department ? card.department.name : "",
        isFlipped: false,
        isSelected: false,
        selectable: false,
        isFalling: false,
        isAnimatingOut: false,
        isAnimatingIn: false,
      }));

      dealOpeningFromDeck(transformedCards, { context: "all-cards fetch" });
    } catch (error) {
      console.error("Error fetching cards:", error);
    }
  };

  const fetchAllCards = async () => {
    try {
      const { data, error } = await supabase
        .from("cards")
        .select("*, department:departments(name)")
        .order("ranking", { ascending: true })
        .order("created_at", { ascending: true });
      console.log("Raw data from Supabase:", data);
      if (error) throw error;
      allCards.value = data.map((card) => ({
        id: card.id,
        title: card.title || "",
        content: card.content || "",
        explanation: card.explanation || "",
        ranking: card.ranking || 0,
        department: card.departmentId, // Use departmentId for the form (foreign key)
        departmentName: card.department ? card.department.name : "Unknown", // Use joined department name for display
        useForDemo: !!card.use_for_demo,
        isFlipped: false,
        isSelected: false,
        selectable: false,
      }));
      console.log("Processed cards with departments:", allCards.value);
    } catch (error) {
      console.error("Error fetching all cards:", error);
    }
  };

  // ✅ Game Controls
  const dealCards = () => {
    if (cards.value.length > 0) {
      cards.value.forEach((card, index) => {
        setTimeout(() => {
          card.isFlipped = true;
        }, index * 300); // Adjust the delay (300ms per card)
      });
  
      // Set the correct timer based on round number
      if (roundNumber.value === 0) {
        // First round - use initial timer
        timeRemaining.value = timerLength.value;
        roundNumber.value = 1;
        // Initialize analytics for new game
        initializeGameAnalytics();
      } else {
        // Subsequent rounds - use secondary timer
        timeRemaining.value = secondaryTimerLength.value;
        markDealt();
      }
      
      if (!timerRunning.value) startTimer();
      gameStarted.value = true;
      allowCardReordering.value = true;
      allowJobDrop.value = false;
      showExplanations.value = false; // Ensure explanations are hidden when game starts
      showTiers.value = false; // Ensure tiers are hidden when game starts
    }
  };

  const startCountdown = () => {
    // Check if countdown feature is enabled
    if (isEnabled('GAME_COUNTDOWN')) {
      showCountdown.value = true;
      countdownNumber.value = 3;
      
      const countdownInterval = setInterval(() => {
        countdownNumber.value--;
        
        if (countdownNumber.value <= 0) {
          clearInterval(countdownInterval);
          showCountdown.value = false;
          dealCards(); // Start the game when countdown reaches 0
        }
      }, 1000); // Count down every second
    } else {
      // Countdown disabled - start game immediately
      dealCards();
    }
  };

  const restartGame = async () => {
    await resetGame(); // Wait for reset to fully complete
    
    // Start countdown after reset is complete
    startCountdown();
  };

  const toggleDoneDiscussion = () => {
    if (isDoneToggled.value) {
      // Undo "I'm Done" - resume the game
      startTimer(); // This will call resumeGameTimer internally
      selectionEnabled.value = false;
      allowJobDrop.value = false;
      maxJobsDoneCount.value -= 3;
      showExplanations.value = false;
      showTiers.value = false;
      isDoneToggled.value = false;
    } else {
      // "I'm Done" clicked - pause and enable selection
      pauseTimer(); // This will call pauseGameTimer internally
      selectionEnabled.value = true;
      allowJobDrop.value = true;
      maxJobsDoneCount.value += 3;
      applyDoneSettings(); // Use the new settings function
      isDoneToggled.value = true;
      
      // Record valid rows at this point
      recordValidRowsAtCurrentState();
    }
  };

  // Legacy method for backward compatibility
  const checkOrder = () => {
    toggleDoneDiscussion();
  };

  /**
   * After 3 cards leave their slots, drop 3 new cards into those gaps, then
   * request a horizontal shuffle + ordered flip (handled with CardContainer).
   */
  const dealMoreCards = () => {
    const row = Array.from({ length: HAND_SIZE }, (_, i) => cards.value[i] ?? null);
    const survivors = row.filter(Boolean).map((card) => ({
      ...card,
      isFlipped: false,
      isSelected: false,
      selectable: false,
      isFalling: false,
      isAnimatingOut: false,
      isAnimatingIn: false,
      reelDelayMs: 0,
    }));
    const gapIndices = row
      .map((card, index) => (card ? -1 : index))
      .filter((index) => index >= 0);

    if (availableCards.value.length < TOP_UP_COUNT || gapIndices.length < TOP_UP_COUNT) {
      cards.value = survivors.map((card) => ({
        ...card,
        isFlipped: true,
        isAnimatingIn: false,
        isAnimatingOut: false,
      }));
      setFinishGame();
      return;
    }

    const { newCards, hand, poolAfter, meta } = buildTopUp(
      survivors,
      availableCards.value,
      TOP_UP_COUNT,
      { maxPerTier: 2 }
    );

    if (newCards.length < TOP_UP_COUNT) {
      cards.value = survivors.map((card) => ({ ...card, isFlipped: true }));
      setFinishGame();
      return;
    }

    availableCards.value = poolAfter;
    trackDealtIds(newCards);
    gameAnalytics.value.cardsDealt =
      (gameAnalytics.value.cardsDealt || 0) + newCards.length;

    const STAGGER_MS = 160;
    const REEL_IN_MS = 1000;
    const SHUFFLE_PAUSE_MS = 120;

    const filled = row.map((card) =>
      card
        ? {
            ...card,
            isFlipped: false,
            isSelected: false,
            selectable: false,
            isFalling: false,
            isAnimatingOut: false,
            isAnimatingIn: false,
            reelDelayMs: 0,
          }
        : null
    );

    newCards.forEach((card, index) => {
      const gapIndex = gapIndices[index];
      filled[gapIndex] = {
        ...card,
        isFlipped: false,
        isSelected: false,
        selectable: false,
        isFalling: false,
        isAnimatingOut: false,
        isAnimatingIn: true,
        reelDelayMs: index * STAGGER_MS,
      };
    });

    cards.value = filled;

    logDeal(`round ${roundNumber.value + 1} gap fill`, {
      meta,
      survivors: dealLogRows(survivors),
      newCards: dealLogRows(newCards),
      gapIndices,
      rows: dealLogRows(cards.value.filter(Boolean)),
      drawPileLeft: availableCards.value.length,
      cardsDealtIds: gameAnalytics.value.cardsDealtIds,
    });

    allowJobDrop.value = false;
    if (!promptsDisabledAfterRound2.value) {
      showPrompts.value = true;
    }
    showExplanations.value = false;
    showTiers.value = false;
    roundNumber.value++;
    timeRemaining.value = secondaryTimerLength.value;
    recordRoundCompletion();
    markDealt();

    const dropDoneAt =
      (newCards.length - 1) * STAGGER_MS + REEL_IN_MS + SHUFFLE_PAUSE_MS;

    // Clear reel-in flags as each new card lands
    filled.forEach((card) => {
      if (!card?.isAnimatingIn) return;
      const startAt = card.reelDelayMs || 0;
      setTimeout(() => {
        card.isAnimatingIn = false;
        card.reelDelayMs = 0;
      }, startAt + REEL_IN_MS);
    });

    // Then shuffle horizontally into the fair hand order, then flip
    setTimeout(() => {
      const target = hand.map((card) => ({
        ...card,
        isFlipped: false,
        isSelected: false,
        selectable: false,
        isFalling: false,
        isAnimatingOut: false,
        isAnimatingIn: false,
        reelDelayMs: 0,
      }));
      logDeal(`round ${roundNumber.value} horizontal shuffle`, {
        rows: dealLogRows(target),
        meta,
      });
      pendingShuffleTo.value = target;
    }, dropDoneAt);
  };

  /** Called by CardContainer after the horizontal FLIP shuffle finishes. */
  const completeDealShuffle = () => {
    pendingShuffleTo.value = null;

    const FLIP_STAGGER_MS = 280;
    const FLIP_MS = 600;

    cards.value.forEach((card, index) => {
      if (!card) return;
      setTimeout(() => {
        card.isFlipped = true;
      }, index * FLIP_STAGGER_MS);
    });

    const lastIndex = Math.max(cards.value.filter(Boolean).length - 1, 0);
    setTimeout(() => {
      if (!timerRunning.value) startTimer();
    }, lastIndex * FLIP_STAGGER_MS + FLIP_MS);
  };
  
  
  
  const toggleDoneSinglePlayer = () => {
    if (isDoneToggled.value) {
      // Undo "I'm Done" - resume the game
      finishGame.value = false;
      startTimer(); // This will call resumeGameTimer internally
      showExplanations.value = false;
      showTiers.value = false;
      isDoneToggled.value = false;
    } else {
      // "I'm Done" clicked - finish game
      finishGame.value = true;
      pauseTimer(); // This will call pauseGameTimer internally
      applyDoneSettings(); // Use the new settings function
      isDoneToggled.value = true;
      
      // Record valid rows at this point
      recordValidRowsAtCurrentState();
      
      // Finalize analytics when user manually ends game
      finalizeGameAnalytics();
    }
  };

  // Legacy method for backward compatibility
  const setFinishGame = ()=> { 
    toggleDoneSinglePlayer();
  }

  // Helper function to apply "I'm Done" settings
  const applyDoneSettings = () => {
    if (showExplanationsWhenDone.value) {
      showExplanations.value = true;
    }
    if (showTiersWhenDone.value) {
      showTiers.value = true;
    }
  }
  

  // Shared helper: fetch and transform cards for a department, respecting demo mode.
  const fetchCardsForReset = async (depId) => {
    applyGameSessionFromStorage(); // ensure isDemoSession is up-to-date

    let query = supabase
      .from("cards")
      .select("*, department:departments(name, front_image, primary_color, secondary_color)");

    if (Number.isFinite(Number(depId))) {
      query = query.eq("departmentId", Number(depId));
    } else {
      query = query.order("ranking", { ascending: true }).order("created_at", { ascending: true });
    }

    if (isDemoSession.value) {
      query = query.eq("use_for_demo", true);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((card) => ({
      id: card.id,
      title: card.title || "",
      content: card.content || "",
      explanation: card.explanation || "",
      ranking: card.ranking || 0,
      department: card.departmentId,
      departmentName: card.department ? card.department.name : "Unknown",
      frontImage: card.front_image || (card.department ? card.department.front_image : ""),
      primaryColor: card.department ? card.department.primary_color : "#000000",
      secondaryColor: card.department ? card.department.secondary_color : "#FFFFFF",
      isFlipped: false,
      isSelected: false,
      selectable: false,
    }));
  };

  const resetGame = async () => {
    // Set resetting state
    isResetting.value = true;
    
    // Stop timer immediately
    stopTimer();
    timeRemaining.value = timerLength.value;
    timerRunning.value = false;

    // If a game had been started, record dealt cards before wiping analytics
    const hadStartedGame =
      !!gameAnalytics.value.gameStartTime ||
      (gameAnalytics.value.cardsDealtIds || []).length > 0;
    if (hadStartedGame) {
      persistDealtHistory();
    }
    
    // Reset game state
    selectionEnabled.value = false;
    resetCounter.value++;
    gameStarted.value = false;
    allowJobDrop.value = false;
    allowCardReordering.value = false;
    showTiers.value = false;
    showExplanations.value = false;
    finishGame.value = false;
    isDoneToggled.value = false;
    roundNumber.value = 0;
    promptsDisabledAfterRound2.value = false;
    showPrompts.value = true;
    
    // Reset analytics state
    gameAnalytics.value = {
      gameStartTime: null,
      gameEndTime: null,
      roundTimes: [],
      totalRoundsCompleted: 0,
      finalCardOrder: [],
      totalGameTime: 0,
      lastResumeTime: null,
      validRowsHistory: [],
      deals: [],
      dealtAt: null,
      cardsDealtIds: [],
      cardsDealt: 0,
    };
    showGameSummary.value = false;
    pendingShuffleTo.value = null;
    
    return new Promise((resolve) => {
      if (cards.value.length > 0) {
        // Flip all cards back with staggered animation
        cards.value.forEach((card, index) => {
          setTimeout(() => { card.isFlipped = false; }, index * 50);
        });
        
        setTimeout(async () => {
          try {
            const newCards = await fetchCardsForReset(selectedDepartment.value);
            jobsDone.value = [];
            dealOpeningFromDeck(newCards, { context: "reset" });
            isResetting.value = false;
            resolve();
          } catch (error) {
            console.error("Error during reset:", error);
            isResetting.value = false;
            resolve();
          }
        }, cards.value.length * 50 + 400);
      } else {
        (async () => {
          try {
            const newCards = await fetchCardsForReset(selectedDepartment.value);
            jobsDone.value = [];
            dealOpeningFromDeck(newCards, { context: "reset" });
            isResetting.value = false;
            resolve();
          } catch (error) {
            console.error("Error during reset:", error);
            isResetting.value = false;
            resolve();
          }
        })();
      }
    });
  };

  // ✅ Card Updates
  const updateCards = (updatedCards) => {
    cards.value = updatedCards;
  };

  const updateJobsDone = (updatedJobsDone) => {
    jobsDone.value = updatedJobsDone;
    const actualCount = updatedJobsDone.filter((card) => card !== null).length;
    if (actualCount >= maxJobsDoneCount.value) {
      selectionEnabled.value = false;
      allowJobDrop.value = false;
    }
  };

  const removeCardFromDeck = (cardId) => {
    cards.value = cards.value.filter((card) => card.id !== cardId);
  };

  // ✅ UI Toggles
  const toggleTiersVisibility = () => {
    showTiers.value = !showTiers.value;
  };

  const toggleExplanations = () => {
    showExplanations.value = !showExplanations.value;
  };

  const toggleShowJobs = () => {
    showJobs.value = !showJobs.value;
  };

  const updateCardHeadingFontSize = (newSize) => {
    cardHeadingFontSize.value = newSize;
    localStorage.setItem('cardHeadingFontSize', newSize);
  };

  // ✅ Card CRUD Operations
  const setEditingCard = (card) => {
    editingCard.value = { ...card };
  };

  const addCard = async (newCard) => {
    try {
      const { department, useForDemo, ...rest } = newCard;
      const insertData = {
        title: rest.title,
        content: rest.content ?? "",
        explanation: rest.explanation ?? "",
        ranking: rest.ranking ?? 0,
        departmentId: department || null,
        use_for_demo: !!useForDemo,
      };

      console.log("Adding card with data:", insertData);

      const { data, error } = await supabase.from("cards").insert([insertData]).select("*, department:departments(name)");
      if (error) throw error;
      if (data && data.length > 0) {
        const rawCard = data[0];
        // Transform the data to match the expected format (same as in loadCards)
        const added = {
          id: rawCard.id,
          title: rawCard.title || "",
          content: rawCard.content || "",
          explanation: rawCard.explanation || "",
          ranking: rawCard.ranking || 0,
          department: rawCard.departmentId,
          departmentName: rawCard.department ? rawCard.department.name : "Unknown",
          useForDemo: !!rawCard.use_for_demo,
          isFlipped: false,
          isSelected: false,
          selectable: false,
        };
        cards.value.push(added);
        allCards.value.push(added);
      }
    } catch (error) {
      console.error("Error adding card:", error);
      throw error;
    }
  };

  const updateCard = async (updatedCard) => {
    try {
      const { id, title, content, explanation, ranking, department, useForDemo } = updatedCard;
      console.log("Updating card in database:", { id, title, content, explanation, ranking, department, useForDemo });

      const updateData = {
        title,
        content,
        explanation,
        ranking,
        departmentId: department || null,
        use_for_demo: !!useForDemo,
      };

      console.log("Update data being sent:", updateData);

      const { error } = await supabase
        .from("cards")
        .update(updateData)
        .eq("id", id);

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      console.log("Card updated successfully in database");

      const index = cards.value.findIndex((card) => card.id === id);
      if (index !== -1) {
        cards.value[index] = { ...cards.value[index], title, content, explanation, ranking, department, useForDemo: !!useForDemo };
        console.log("Updated card in local state:", cards.value[index]);
      }

      const allCardsIndex = allCards.value.findIndex((card) => card.id === id);
      if (allCardsIndex !== -1) {
        allCards.value[allCardsIndex] = {
          ...allCards.value[allCardsIndex],
          title,
          content,
          explanation,
          ranking,
          department,
          useForDemo: !!useForDemo,
        };
        console.log("Updated card in allCards:", allCards.value[allCardsIndex]);
        console.log("All cards array after update:", allCards.value);
      } else {
        console.log("Card not found in allCards with id:", id);
        console.log("Available card IDs:", allCards.value.map(card => card.id));
      }
    } catch (error) {
      console.error("Error updating card:", error);
      throw error;
    }
  };

  const deleteCard = async (cardId) => {
    try {
      const { error } = await supabase.from("cards").delete().eq("id", cardId);
      if (error) throw error;
      cards.value = cards.value.filter((card) => card.id !== cardId);
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  //NEW SELECT FUNCTIONS

  // Helper: Add or remove a card from selection
  const toggleSelectedCard = (card) => {
    const index = selectedCards.value.findIndex(c => c.id === card.id);
    if (index === -1) {
      // Add card only if less than 3 are already selected
      if (selectedCards.value.length < 3) {
        selectedCards.value.push(card);
      }
    } else {
      // Remove if already selected
      selectedCards.value.splice(index, 1);
    }
  };

  // Helper: Move selected cards into jobsDone array at next available slots
  const moveSelectedCardsToJobsDone = () => {
    // Only proceed if exactly 3 cards are selected
    if (selectedCards.value.length === 3) {
      const cardsToMove = [...selectedCards.value];
      const selectedCardIds = new Set(cardsToMove.map((card) => card.id));

      // Mark selected cards animating out; hide survivors so they cannot be tracked
      cards.value.forEach((card) => {
        if (!card) return;
        if (selectedCardIds.has(card.id)) {
          card.isAnimatingOut = true;
        } else {
          card.isFlipped = false;
        }
      });

      selectedCards.value = [];
      selectionEnabled.value = false;
      isDoneToggled.value = false;

      const OUT_MS = 800;

      setTimeout(() => {
        cardsToMove.forEach((card) => {
          const nextIndex = jobsDone.value.findIndex((slot) => slot === null);
          if (nextIndex !== -1) {
            jobsDone.value[nextIndex] = card;
          } else {
            jobsDone.value.push(card);
          }
        });

        jobsDone.value = [...jobsDone.value].sort((a, b) => {
          if (a === null) return 1;
          if (b === null) return -1;
          return a.ranking - b.ranking;
        });

        // Leave gaps where the 3 cards were so new cards can drop into those slots
        cards.value = cards.value.map((card) => {
          if (!card) return null;
          if (selectedCardIds.has(card.id)) return null;
          return {
            ...card,
            isFlipped: false,
            isAnimatingOut: false,
            isAnimatingIn: false,
            reelDelayMs: 0,
          };
        });

        dealMoreCards();
      }, OUT_MS);
    }
  };
  
  


  return {
    timerLength,
    secondaryTimerLength,
    timeRemaining,
    timerRunning,
    isTimerZero,
    startTimer,
    pauseTimer,
    stopTimer,
    updateTimer,
    updateSecondaryTimer,
    canDealMoreCards,
    noMoreCards,
    cards,
    allCards,
    availableCards,
    jobsDone,
    showTiers,
    selectionEnabled,
    resetCounter,
    gameStarted,
    isDiscussionMode,
    allowJobDrop,
    allowCardReordering,
    maxJobsDoneCount,
    showPrompts,
    promptsDisabledAfterRound2,
    showExplanations,
    activeModal,
    openModal: (modalType) => (activeModal.value = modalType),
    closeModal: () => (activeModal.value = null),
    fetchCards,
    fetchAllCards,
    dealCards,
    checkOrder,
    dealMoreCards,
    pendingShuffleTo,
    completeDealShuffle,
    resetGame,
    restartGame,
    setFinishGame,
    finishGame,
    updateCards,
    updateJobsDone,
    removeCardFromDeck,
    toggleTiersVisibility,
    toggleExplanations,
    setEditingCard,
    updateCard,
    addCard,
    deleteCard,
    selectedCards, //ADDED
    toggleSelectedCard,//ADDED
    moveSelectedCardsToJobsDone,//ADDED
    selectedDepartment,
    setSelectedDepartment,
    isDemoSession,
    applyGameSessionFromStorage,
    fetchCardsByDepartment,
    currentDepartmentPrimaryColor,
    fetchDepartments,
    isDoneToggled, // NEW: Toggle state
    toggleDoneDiscussion, // NEW: Toggle for discussion mode
    toggleDoneSinglePlayer, // NEW: Toggle for single player mode
    roundNumber, // NEW: Track round progression
    showJobs,
    toggleShowJobs,
    cardHeadingFontSize,
    updateCardHeadingFontSize,
    isResetting,
    showExplanationsWhenDone, // NEW: Control explanations
    showTiersWhenDone, // NEW: Control tiers
    showCountdown, // NEW: Countdown overlay state
    countdownNumber, // NEW: Current countdown number
    startCountdown, // NEW: Function to start countdown
    // Notes API
    notes,
    sessionId,
    ensureNotesSessionId,
    fetchNotes,
    addNote,
    updateNote,
    deleteNote,
    subscribeToNotes,
    unsubscribeFromNotes,
    // Analytics API
    gameAnalytics,
    sessionStats,
    showGameSummary,
    averageRoundTime,
    validRowsCount,
    initializeGameAnalytics,
    recordRoundCompletion,
    recordValidRowsAtCurrentState,
    finalizeGameAnalytics,
    pauseGameTimer,
    resumeGameTimer,
    countValidCardRows,
    isValidCardRow
  };
});
